/**
 * Pure rules for the new-website-order alarm.
 *
 * Kept free of DOM, audio, timers and React so the logic that decides whether
 * the tablet starts making noise can be tested directly. Everything here is a
 * pure function of (orders, what we have already alerted on, the clock).
 */

import {
  ALERT_HOURS,
  ALERT_MAX_ORDER_AGE_MS,
  ALERT_ACTIVE_STATUSES,
} from './constants';

// Stamped by the ordering backend on anything placed through the customer site.
// Never selectable at the POS — see ORDER_SOURCES in constants.js.
export const WEBSITE_ORDER_SOURCE = 'website';

/**
 * Is `date` inside the shop's alerting window?
 *
 * Hours are read in the device's local time, which on the shop tablet is
 * Africa/Accra. Windows that wrap midnight (e.g. 20 -> 7) are supported so the
 * constant can be flipped to an overnight shop without touching this code.
 */
export function isWithinAlertHours(date, hours = ALERT_HOURS) {
  const { start, end } = hours || {};
  if (!Number.isFinite(start) || !Number.isFinite(end)) return true;
  // Equal bounds mean "always on" rather than "never" — an accidental
  // { start: 0, end: 0 } should not silently disable every alarm.
  if (start === end) return true;
  const hour = date.getHours();
  if (start < end) return hour >= start && hour < end;
  return hour >= start || hour < end;
}

/** Milliseconds since epoch for an order, or null if it has no usable time. */
function getOrderPlacedAt(order) {
  const raw = order?.timestamp;
  const ms = typeof raw === 'number' ? raw : Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

/** Did this order come from the customer website? */
export function isWebsiteOrder(order) {
  return order?.orderSource === WEBSITE_ORDER_SOURCE;
}

/**
 * Wording for the banner and the push body. The alarm fires on order creation
 * regardless of payment, so staff have to be told which they are looking at —
 * an unpaid one still needs confirming.
 */
export function getPaymentStateLabel(order) {
  return order?.status === 'pending_payment' ? 'Unpaid' : 'Paid';
}

/**
 * Should this one order raise the alarm at `now`?
 */
export function isAlertableOrder(order, now = new Date(), options = {}) {
  const {
    hours = ALERT_HOURS,
    maxAgeMs = ALERT_MAX_ORDER_AGE_MS,
    activeStatuses = ALERT_ACTIVE_STATUSES,
  } = options;

  if (!order || order.id == null) return false;
  if (!isWebsiteOrder(order)) return false;

  // An order that has already moved on has been handled by someone. A push or
  // a re-sync arriving late about it must not restart the alarm.
  if (!activeStatuses.includes(order.status)) return false;

  const placedAt = getOrderPlacedAt(order);
  if (placedAt === null) return false;

  // A negative age means the tablet's clock is behind the server's. Treat that
  // as fresh rather than dropping a real order on the floor.
  if (now.getTime() - placedAt > maxAgeMs) return false;

  return isWithinAlertHours(now, hours);
}

/**
 * Which orders should start alarming right now, given the ones already alerted.
 *
 * There is deliberately no "first load" baseline here: the age cutoff plus the
 * persisted ledger already prevent a backlog from ringing, and dropping the
 * baseline means a genuinely fresh order that landed two minutes before someone
 * logged in still gets the alarm it deserves.
 */
export function selectAlertableOrders(orders, alertedIds, now = new Date(), options = {}) {
  if (!Array.isArray(orders)) return [];
  const seen = alertedIds instanceof Set ? alertedIds : new Set(alertedIds || []);
  return orders.filter(
    (order) => !seen.has(String(order?.id)) && isAlertableOrder(order, now, options)
  );
}

/**
 * Headline + per-order lines for the alert banner.
 */
export function buildAlertSummary(orders) {
  const list = Array.isArray(orders) ? orders : [];
  const count = list.length;
  return {
    count,
    title: count === 1 ? 'New website order' : `${count} new website orders`,
    lines: list.map((order) => ({
      id: String(order.id),
      reference: `#${String(order.id).slice(-6)}`,
      customerName: order.customerName || 'Website customer',
      // null means "not known yet" — a placeholder for an order that is
      // alarming but absent from the latest fetch. The banner omits those
      // fields rather than showing a confident GHS 0.00.
      total: order.isPlaceholder ? null : Number(order.total || 0),
      paymentState: order.isPlaceholder ? null : getPaymentStateLabel(order),
    })),
  };
}

/**
 * Should a running alarm sound again?
 *
 * Pulled out of the hook so the two rules that stop the noise — the hard cap and
 * closing time — are testable without a hook-testing harness. Both are checked
 * on every repeat rather than once at the start, so an alarm that began at 19:59
 * falls silent at 20:00 instead of running its full budget into a closed shop.
 */
export function shouldContinueRinging({ now, deadline, hours = ALERT_HOURS }) {
  const at = now instanceof Date ? now : new Date(now);
  if (!Number.isFinite(deadline) || at.getTime() >= deadline) return false;
  return isWithinAlertHours(at, hours);
}
